import { useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import httpClient from '../../../httpClient';
import ChevronIcon from '../../../images/chevron-down-12.svg';
import styles from '../../../pages/ProductSelection.module.css';
import useProductSelectionStore from '../../../stores/useProductSelectionStore';
import useAlbumSelectionStore from '../../../stores/useAlbumSelectionStore';
import { COMING_SOON_ALBUM_TYPES, MUSEUM_CASE_ORDER_HERE_URL, DOUBLE_FOLIO_ORDER_HERE_URL } from '../../../lib/const';

const FormInputs = ({ quantityRef, onInputChange, onTextInputChange, orderId, lineItemId }) => {
  const {
    form: printForm,
    productType,  
    setForm,
    defaultOptions,
    selectedPhoto,
    lineItem,
    resetPrintSizeForm,
    setPrintSizeLoading,
    processPrintSizeData,
    setFinishingServiceLoading,
    setFrameInputsLoading,
    processFinishingCategoriesData,
    processFinishingServicesData,
    processFinishingDetailsData,
    processFinishingServiceChange,
    processFrameSelection,
    processFrameMatSelection,
    getBaseMeta,
  } = useProductSelectionStore();

  const {
    albumForm,
    processAlbumTypesData,
    processAlbumPapersData,
    processAlbumSizesData,
    processFabricsData,
    processStampingsData,
    processPreDesignedCoversData,
    processFontOptionsData,
    setAlbumPaperLoading,
    setAlbumSizeLoading,
    setFabricsLoading,
    setStampingsLoading,
    setPreDesignedCoverLoading,
    setFontOptionsLoading,
    initializeAlbumForm,
  } = useAlbumSelectionStore();

  const form = productType === 'albums' ? albumForm : printForm;
  const selectRef = useRef(null);

  const paperTypeSelectedValue = form?.inputs?.paperType?.selectedValue;
  const printSizeSelectedValue = form?.inputs?.printSize?.selectedValue;
  const finishingCategoryId = form?.meta?.finishingCategoryId;
  const albumTypeSelectedValue = albumForm?.inputs?.albumType?.selectedValue;
  const isComingSoonAlbumType = COMING_SOON_ALBUM_TYPES.includes(albumTypeSelectedValue);
  const albumPaperSelectedValue = albumForm?.inputs?.albumPaper?.selectedValue;
  const albumSizeSelectedValue = albumForm?.inputs?.albumSize?.selectedValue;
  const coverFabricSelectedValue = albumForm?.inputs?.coverFabric?.selectedValue;
  const interiorCoverFabricSelectedValue = albumForm?.inputs?.interiorFabric?.selectedValue;
  const debossingTypeSelectedValue = albumForm?.inputs?.debossingType?.selectedValue;
  const clamshellUpgradeSelectedValue = albumForm?.inputs?.clamshellUpgrade?.selectedValue;

  // Helper to get base meta object
  const getBaseMetaLocal = (prevState) => {
    if (getBaseMeta) {
      return getBaseMeta(prevState);
    }
    return {
      subtotal: prevState.meta.subtotal,
      xAspectRatio: prevState.meta.xAspectRatio,
      yAspectRatio: prevState.meta.yAspectRatio,
      aspectRatio: prevState.meta.aspectRatio,
      cropPositionX: prevState.meta.cropPositionX,
      cropPositionY: prevState.meta.cropPositionY,
      cropWidth: prevState.meta.cropWidth,
      cropHeight: prevState.meta.cropHeight,
      finishingCategoryId: prevState.meta.finishingCategoryId,
      finishingServiceId: prevState.meta.finishingServiceId,
      finishingDetailId: prevState.meta.finishingDetailId,
    };
  };

  // Helper to filter options
  const filterOption = (option, key) => {
    if (key === 'paperType') {
      if (productType === 'prints') {
        return !option.text.toLowerCase().includes('canvas');
      }
      if (productType === 'canvas') {
        return option.text.toLowerCase().includes('canvas');
      }
      if (productType === 'albums') {
        return option.text.toLowerCase().includes('albums');
      }
    }
    const textFields = ['acrylic', 'frameStain', 'mountColor', 'frameMat'];
    if (textFields.includes(key)) {
      return option?.text && option.text.trim() !== '';
    }
    return true;
  };

  const printSizesQuery = useQuery({
    queryKey: ['printSizes', paperTypeSelectedValue],
    queryFn: () => httpClient.get(`products/${paperTypeSelectedValue}/print_sizes`),
    enabled: !!paperTypeSelectedValue && paperTypeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processPrintSizeData(response);
    },
  });

  // reset form when paper type is default
  useEffect(() => {
    if (paperTypeSelectedValue === 'default') {
      resetPrintSizeForm();
    } else if (paperTypeSelectedValue && paperTypeSelectedValue !== 'default' && printSizesQuery.isLoading) {
      setPrintSizeLoading(true);
    }
  }, [paperTypeSelectedValue, printSizesQuery.isLoading]);

  // finishing categories query
  const finishingCategoriesQuery = useQuery({
    queryKey: ['finishingCategories', printSizeSelectedValue],
    queryFn: () => httpClient.get(`products/print_sizes/${printSizeSelectedValue}/finishing_categories`),
    enabled: !!printSizeSelectedValue && printSizeSelectedValue !== 'default',
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFinishingCategoriesData(response);
    },
  });
  
  // set finishing service loading when fetching
  useEffect(() => {
    if (printSizeSelectedValue && printSizeSelectedValue !== 'default' && finishingCategoriesQuery.isLoading) {
      setFinishingServiceLoading(true);
    }
  }, [printSizeSelectedValue, finishingCategoriesQuery.isLoading]);

  // Finishing services query
  const finishingServicesQuery = useQuery({
  queryKey: ['finishingServices', printSizeSelectedValue, finishingCategoryId],
  queryFn: () =>
    httpClient.get(
      `products/print_sizes/${printSizeSelectedValue}/finishing_categories/${finishingCategoryId}/finishing_services`
    ),
  enabled: !!printSizeSelectedValue && !!finishingCategoryId,
  staleTime: Infinity,
  cacheTime: 0,
  onSuccess: (response) => {
    console.log('Finishing services API response:', response);
    processFinishingServicesData(response);
  },
});

  // set frame inputs loading when fetching finishing services for framing
  useEffect(() => {
    if (finishingCategoryId && finishingCategoryId !== 'default' && finishingServicesQuery.isLoading) {
      setFrameInputsLoading(true);
    }
  }, [finishingCategoryId, finishingServicesQuery.isLoading]);

  // Finishing details query
  useQuery({
    queryKey: ['finishingDetailsOptions', form.meta.finishingServiceId],
    queryFn: () => httpClient.get(`products/${form.meta.finishingServiceId}/finishing_details`),
    enabled: !!form.meta.finishingServiceId,
    staleTime: Infinity,
    cacheTime: 0,
    onSuccess: (response) => {
      processFinishingDetailsData(response);
    },
  });

  // Fetch album types
  const albumTypesQuery = useQuery({
    queryKey: ['albumTypes'],
    queryFn: () => httpClient.get('albums/types'),
    enabled: productType === 'albums',
    staleTime: Infinity,
    onSuccess: (response) => {
      processAlbumTypesData(response);
    },
  });

  useEffect(() => {
    if (productType === 'albums' && albumTypesQuery.data && !albumTypesQuery.isLoading) {
      processAlbumTypesData(albumTypesQuery.data);
    }
  }, [productType, albumTypesQuery.data, albumTypesQuery.isLoading, processAlbumTypesData]);

  // Fetch album papers based on selected album type
  const albumPapersQuery = useQuery({
    queryKey: ['albumPapers', albumTypeSelectedValue],
    queryFn: () => httpClient.get(`albums/album_papers?album_type_id=${albumTypeSelectedValue}`),
    enabled: productType === 'albums' && !!albumTypeSelectedValue && albumTypeSelectedValue !== 'default' && !isComingSoonAlbumType,
    staleTime: Infinity,
    onSuccess: (response) => {
      processAlbumPapersData(response);
    },
  }); 

  // Set album paper loading when fetching
  useEffect(() => {
    if (productType === 'albums' && albumTypeSelectedValue && albumTypeSelectedValue !== 'default') {
      setAlbumPaperLoading(albumPapersQuery.isLoading);
    }
  }, [productType, albumTypeSelectedValue, albumPapersQuery.isLoading]);

  // Fetch album sizes based on selected album paper
  const albumSizesQuery = useQuery({
    queryKey: ['albumSizes', albumPaperSelectedValue],
    queryFn: () => httpClient.get(`albums/sizes?album_paper_id=${albumPaperSelectedValue}`),
    enabled: productType === 'albums' && !!albumPaperSelectedValue && albumPaperSelectedValue !== 'default',
    staleTime: Infinity,
    onSuccess: (response) => {
      processAlbumSizesData(response);
    },
  });

  // Set album size loading when fetching
  useEffect(() => {
    if (productType === 'albums' && albumPaperSelectedValue && albumPaperSelectedValue !== 'default' && albumSizesQuery.isLoading) {
      setAlbumSizeLoading(true);
    }
  }, [productType, albumPaperSelectedValue, albumSizesQuery.isLoading]);

  // Fetch fabrics (can be fetched once for all fabric fields)
  const fabricsQuery = useQuery({
    queryKey: ['fabrics', albumSizeSelectedValue ],
    queryFn: () => httpClient.get('albums/fabrics'),
    enabled: productType === 'albums' && !!albumSizeSelectedValue && albumSizeSelectedValue !== 'default',
    staleTime: Infinity,
    onSuccess: (response) => {
      processFabricsData(response);
    },
  });

  // Set fabrics loading when fetching
  useEffect(() => {
    if (productType === 'albums' && albumSizeSelectedValue && albumSizeSelectedValue !== 'default' && fabricsQuery.isLoading) {
      setFabricsLoading(true);
    }
  }, [productType, albumSizeSelectedValue, fabricsQuery.isLoading]);

  // Fetch stampings for debossing options
  const stampingsQuery = useQuery({
    queryKey: ['stampings', coverFabricSelectedValue, interiorCoverFabricSelectedValue],
    queryFn: () => httpClient.get('albums/stampings'),
    enabled: productType === 'albums' && ((!!interiorCoverFabricSelectedValue && interiorCoverFabricSelectedValue !== 'default')
    ) && !!coverFabricSelectedValue && coverFabricSelectedValue !== 'default',
    staleTime: Infinity,
    onSuccess: (response) => {
      processStampingsData(response);
    },
  });
  // Set stampings loading when fetching
  useEffect(() => {
    if (productType === 'albums' && coverFabricSelectedValue && coverFabricSelectedValue !== 'default' && stampingsQuery.isLoading) {
      setStampingsLoading(true);
    }
  }, [productType, coverFabricSelectedValue, stampingsQuery.isLoading]);

  // Fetch pre-designed covers (optional field, fetched when album type is selected)
  const preDesignedCoversQuery = useQuery({
    queryKey: ['preDesignedCovers'],
    queryFn: () => httpClient.get('albums/pre_designed_covers'),
    enabled: productType === 'albums' && !!albumTypeSelectedValue && albumTypeSelectedValue !== 'default' && !isComingSoonAlbumType,
    staleTime: Infinity,
    onSuccess: (response) => {
      processPreDesignedCoversData(response);  
    },
  });

  // Set pre-designed cover loading when fetching
  useEffect(() => {
    if (productType === 'albums' && albumTypeSelectedValue && albumTypeSelectedValue !== 'default') {
      setPreDesignedCoverLoading(preDesignedCoversQuery.isLoading);
    }
  }, [productType, albumTypeSelectedValue, preDesignedCoversQuery.isLoading, setPreDesignedCoverLoading]);

  // Fetch font options (for clamshell cover text font selection)
  const fontOptionsQuery = useQuery({
    queryKey: ['fontOptions'],
    queryFn: () => httpClient.get('albums/font_options'),
    enabled: productType === 'albums' && !!albumTypeSelectedValue && albumTypeSelectedValue !== 'default' && !isComingSoonAlbumType,
    staleTime: Infinity,
    onSuccess: (response) => {
      processFontOptionsData(response);
    },
  });

  // Set font options loading when fetching
  useEffect(() => {
    if (productType === 'albums' && albumTypeSelectedValue && albumTypeSelectedValue !== 'default' && fontOptionsQuery.isLoading) {
      setFontOptionsLoading(true);
    }
  }, [productType, albumTypeSelectedValue, fontOptionsQuery.isLoading, setFontOptionsLoading]);

  // Initialize expanded album inputs when album type is chosen (avoid re-running on text-only updates)
  useEffect(() => {
    if (productType !== 'albums') return;
    if (!albumTypeSelectedValue || albumTypeSelectedValue === 'default') return;

    const albumTypeText = albumForm?.inputs?.albumType?.selectedValueText;
    if (albumTypeText) {
      initializeAlbumForm(albumTypeSelectedValue, albumTypeText);
    }
  }, [productType, albumTypeSelectedValue, initializeAlbumForm]);

  // Process finishing service selection
  useEffect(() => {
    if (form.inputs.finishingService?.selectedValueText === undefined) return;
    processFinishingServiceChange(form.inputs.finishingService.selectedValueText);
  }, [form?.inputs?.finishingService?.selectedValueText]);

  // set finishing category ID when matFormat or frameType changes
  useEffect(() => {
    if ((form.inputs.matFormat?.selectedValue === undefined && form.inputs.frameType?.selectedValue === undefined)) return;
    let finishingCategoryIdValue;
    let error;
    if (form.inputs.matFormat?.selectedValue !== undefined) {
      finishingCategoryIdValue = form.inputs.matFormat.selectedValue;
      error = 'Please fill Mat Size';
    } else if (form.inputs.frameType?.selectedValue !== undefined) {
      // If frameType is 'default'
      if (form.inputs.frameType.selectedValue === 'default') {
        setForm((prevState) => ({
          meta: {
            ...getBaseMetaLocal(prevState),
            finishingCategoryId: undefined,
            finishingServiceId: undefined,
            finishingDetailId: undefined,
            requiredInputs: ['Frame Type'],
          },
          inputs: {
            paperType: prevState.inputs.paperType,
            printSize: prevState.inputs.printSize,
            finishingService: prevState.inputs.finishingService,
            frameType: prevState.inputs.frameType,
          },
        }));
        return;
      }
      finishingCategoryIdValue = form.inputs.frameType.selectedValue;
      error = 'Please fill Frame, Frame Mat, Acrylic and Frame Stain';
    }

    setForm((prevState) => ({
      meta: {
        ...getBaseMetaLocal(prevState),
        finishingCategoryId: finishingCategoryIdValue,
        error,
      },
      inputs: prevState.inputs,
    }));
  }, [form?.inputs?.matFormat?.selectedValue, form?.inputs?.frameType?.selectedValue]);

  // process frame selection to get frame mats
  useEffect(() => {
    if (form.inputs.frame?.selectedValue === undefined) return;
    console.log('form.inputs.frame.selectedValue', form.inputs.frame.selectedValue);
    processFrameSelection(form.inputs.frame.selectedValue, form.inputs.frame.selectedValueText);
  }, [form?.inputs?.frame?.selectedValue]);

  // process frame mat selection to get acrylics
  useEffect(() => {
    if (form.inputs.frameMat?.selectedValue === undefined) return;
    processFrameMatSelection(form.inputs.frameMat.selectedValue, form.inputs.frameMat.selectedValueText);
  }, [form?.inputs?.frameMat?.selectedValue]);

  // handle acrylic and mount size selection
  useEffect(() => {
    if (form.inputs.acrylic?.selectedValue === undefined && form.inputs.mountSize?.selectedValue === undefined) return;
    if (form.inputs.acrylic?.selectedValue === 'default') {
      setForm((prevState) => ({
        meta: {
          subtotal: prevState.meta.subtotal,
          xAspectRatio: prevState.meta.xAspectRatio,
          yAspectRatio: prevState.meta.yAspectRatio,
          aspectRatio: prevState.meta.aspectRatio,
          cropPositionX: prevState.meta.cropPositionX,
          cropPositionY: prevState.meta.cropPositionY,
          cropWidth: prevState.meta.cropWidth,
          cropHeight: prevState.meta.cropHeight,
          finishingCategoryId: prevState.meta.finishingCategoryId,
          requiredInputs: ['Acrylic',
            ...((prevState.inputs?.frameType?.selectedValueText === 'Hardwood Frame' 
              || prevState.inputs?.frameType?.selectedValueText === 'Hardwood Float Canvas Frame' )
              ? ['Frame Stain'] : [])
          ],
        },
        inputs: {
          paperType: prevState.inputs.paperType,
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
          frameType: prevState.inputs.frameType,
          frame: prevState.inputs.frame,
          frameMat: prevState.inputs.frameMat,
          acrylic: prevState.inputs.acrylic,
          ...((form.inputs?.frameType?.selectedValueText === 'Hardwood Frame' 
            || form.inputs?.frameType?.selectedValueText === 'Hardwood Float Canvas Frame' )
            && { frameStain: defaultOptions.frameStain }),
        },
      }));
      return;
    }

    if (form.inputs.mountSize?.selectedValue === 'default') {
      setForm((prevState) => ({
        meta: {
          ...getBaseMetaLocal(prevState),
          requiredInputs: ['Mount Size', 'Mount Color'],
        },
        inputs: {
          paperType: prevState.inputs.paperType,  
          printSize: prevState.inputs.printSize,
          finishingService: prevState.inputs.finishingService,
          mountSize: prevState.inputs.mountSize,
          mountColor: defaultOptions.mountColor,
        },
      }));
      return;
    }

    let finishingServiceId;

    if (form.inputs.acrylic?.selectedValue !== undefined && form.inputs.acrylic?.selectedValue !== 'none' && form.inputs.acrylic?.selectedValue !== 'default') {
      finishingServiceId = form.inputs.acrylic.selectedValue;
    } else if (form.inputs.mountSize?.selectedValue !== undefined && form.inputs.mountSize?.selectedValue !== 'default') {
      finishingServiceId = form.inputs.mountSize.selectedValue;
    }

    setForm((prevState) => ({
      ...prevState,
      meta: {
        ...getBaseMetaLocal(prevState),
        finishingServiceId: finishingServiceId ?? prevState.meta.finishingServiceId,
        requiredInputs: [],
      },
    }));
  }, [form?.inputs?.acrylic?.selectedValue, form?.inputs?.mountSize?.selectedValue]);

  // Handle frame stain and mount color selection
  useEffect(() => {
    if ((form.inputs.frameStain?.selectedValue === undefined && form.inputs.mountColor?.selectedValue === undefined)) return;

    const finishingDetailId = form.inputs.frameStain?.selectedValue || form.inputs.mountColor?.selectedValue;

    setForm((prevState) => ({
      meta: {
        ...getBaseMetaLocal(prevState),
        finishingDetailId,
        requiredInputs: [],
      },
      inputs: prevState.inputs,
    }));
  }, [form?.inputs?.frameStain?.selectedValue, form?.inputs?.mountColor?.selectedValue]);

  // Handle mat size selection
  useEffect(() => {
    if (form.inputs.matSize?.selectedValue === undefined) return;

    const finishingServiceIdValue = form.inputs.matSize.selectedValue;

    setForm((prevState) => ({
      meta: {
        ...prevState.meta,
        finishingServiceId: finishingServiceIdValue,
        requiredInputs: [],
      },
      inputs: prevState.inputs,
    }));
  }, [form?.inputs?.matSize?.selectedValue]);

  // Update URL when finishing detail is set
  useEffect(() => {
    if (lineItemId) return;
    if (!form || !form.meta) return;
    if (!orderId) return;
    if (form.meta.finishingDetailId) {
      window.history.pushState(
        '',
        '',
        `/product-selection/${orderId}?selectedPhoto=${selectedPhoto?.attributes?.guid}&finishingService=${form.meta.finishingCategoryId}&finishingDetail=${form.meta.finishingDetailId}`,
      );
    }
  }, [form?.meta?.finishingDetailId, orderId, selectedPhoto?.attributes?.guid, form?.meta?.finishingCategoryId]);

  // Early return if form or form.inputs is not available
  if (!form || !form.inputs) return null;

  // Use 2-column grid for albums, 4-column for prints/canvas
  const gridTemplateColumns = productType === 'albums' ? '1fr 1fr' : 'auto auto auto auto';
  
  return (
    <div
      className="accordion-item-block"
      style={{ gridTemplateColumns, gridGap: '15px' }}
    >
      {/* Quantity Input - Only show for prints/canvas, not for albums */}
      {productType !== 'albums' && (
        <div className="input-container quantity">
          <div className="label-container">
            <div className="div-block-150">
              <div className="sub-label-form">Quantity</div>
            </div>
          </div>
          <div className="form-block-input w-form">
            <div className="input-wrapper">
              <input
                ref={quantityRef}
                type="number"
                min="1"
                maxLength="256"
                name="field-4"
                data-name="Field 4"
                id="data-jscolor"
                defaultValue="1"
                className="text-field-2 data-jscolor w-input"
                pattern="[0-9]*"
                inputMode="numeric"
              />
            </div>
          </div>
        </div>
      )}
      {/* Dynamic Form Inputs */}
      {Object.keys(form.inputs)
        .filter((key) => {
          if (productType === 'albums' && isComingSoonAlbumType) {
            return key === 'albumType';
          }
          if (form.inputs[key]?.isHidden) {
            return false;
          }
          return true;
        })
        .map((key) => {
        const input = form.inputs[key];
        if (!input) return null;
        
        const inputType = input.inputType || 'select';
        
        return (
          <div
            key={key}
            style={input.style}
          >
            <div className="input-container paper-type">
              <div className="label-container">
                <div className="div-block-150">
                  {input.labelHtml ? (
                    <div 
                      className="sub-label-form"
                      dangerouslySetInnerHTML={{ __html: input.labelHtml }}
                    />
                  ) : (
                    <div className="sub-label-form">
                      {key === 'printSize' && productType === 'canvas' 
                        ? 'CANVAS SIZE' 
                        : input.label}
                    </div>
                  )}
                </div>
              </div>
              <div className="form-block-input w-form">
                {inputType === 'text' ? (
                  <form className="form-input select-field">
                    <div className="input-wrapper">
                      <input
                        type="text"
                        className="text-field-2 w-input"
                        maxLength="256"
                        name={`field-${key}`}
                        placeholder={input.placeholder || ''}
                        value={input.selectedValue || ''}
                        onChange={(e) => onTextInputChange ? onTextInputChange(key, e.target.value) : onInputChange(key, e)}
                        disabled={input.isDisabled}
                      />
                    </div>
                  </form>
                ) : (
                  <form className="form-input select-field">
                    <select
                      ref={selectRef}
                      id="my-field-3"
                      name="field-3"
                      data-name="Field 3"
                      className={`form-select-field w-select select-field-wrapper ${styles.selectNoNativeArrow}`}
                      style={{ width: '100%' }}
                      onChange={(e) => onInputChange(key, e)}
                      value={input.selectedValue || 'default'}
                      disabled={key === 'clamshellUpgrade' && !!input.isDisabled}
                      title={
                        key === 'clamshellUpgrade' && input.isDisabled && input.disabledMessage
                          ? input.disabledMessage
                          : undefined
                      }
                    >
                      {input.isLoading === true && (!input.options || input.options.length === 0) ? (
                        <option value="">Loading...</option>
                      ) : (
                        <option value="default">Choose One</option>
                      )}
                      {input.options && input.options.length > 0 && input.options
                        .filter((option) => filterOption(option, key))
                        .filter((option) => !option.text?.endsWith('**'))
                        .map((option) => {
                          if (!option || !option.value || !option.text) return null;
                          const isSizeDropdown = key === 'printSize' || key === 'matSize' || key === 'mountSize' || key === 'albumSize' || key === 'debossingType' || key === 'clamshellDebossingType' || key === 'clamshellUpgrade';
                          const displayText = isSizeDropdown
                            ? option.text
                            : option.text.replace(/\(\$\d+(\.\d{1,2})?\)/g, '').trim();
                          return (
                            <option
                              key={option.value}
                              value={option.value}
                            >
                              {displayText}
                            </option>
                          );
                        })
                        .filter(Boolean)
                      }
                    </select>
                    <img
                      src={ChevronIcon}
                      alt=""
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', width: '16px', height: '16px' }}
                    />
                  </form>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FormInputs;
